import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { ReviewResult } from '../../api/types';

export interface ReviewPanelProps {
  result?: ReviewResult;
  isLoading?: boolean;
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.background.default,
  },
  passedIcon: {
    color: theme.palette.success.main,
    fontSize: '1.3rem',
  },
  failedIcon: {
    color: theme.palette.secondary.main,
    fontSize: '1.3rem',
  },
  summary: {
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.04em',
      padding: theme.spacing(0.75, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& td': {
      fontSize: '0.82rem',
      padding: theme.spacing(0.75, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  chipError: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    fontWeight: 600,
    height: 20,
    fontSize: '0.65rem',
  },
  chipWarning: {
    backgroundColor: theme.palette.warning.main,
    color: '#fff',
    fontWeight: 600,
    height: 20,
    fontSize: '0.65rem',
  },
  chipInfo: {
    backgroundColor: theme.palette.grey[500],
    color: '#fff',
    fontWeight: 600,
    height: 20,
    fontSize: '0.65rem',
  },
  ruleCell: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  loadingSkeleton: {
    padding: theme.spacing(2),
  },
}));

export const ReviewPanel = ({ result, isLoading }: ReviewPanelProps) => {
  const classes = useStyles();

  if (isLoading) {
    return (
      <div className={classes.root} data-testid="review-panel-loading">
        <div className={classes.loadingSkeleton}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton
            variant="rect"
            width="100%"
            height={60}
            style={{ marginTop: 8 }}
          />
        </div>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const severityChipClass = {
    error: classes.chipError,
    warning: classes.chipWarning,
    info: classes.chipInfo,
  };

  return (
    <div className={classes.root} data-testid="review-panel">
      <div className={classes.header}>
        {result.passed ? (
          <CheckCircleOutlineIcon className={classes.passedIcon} />
        ) : (
          <HighlightOffIcon className={classes.failedIcon} />
        )}
        <Typography className={classes.summary}>{result.summary}</Typography>
      </div>

      {result.issues.length > 0 && (
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Severidade</TableCell>
              <TableCell>Arquivo</TableCell>
              <TableCell>Mensagem</TableCell>
              <TableCell>Regra</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.issues.map((issue, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Chip
                    label={issue.severity}
                    size="small"
                    className={severityChipClass[issue.severity]}
                  />
                </TableCell>
                <TableCell>
                  {issue.file || '-'}
                  {issue.line ? `:${issue.line}` : ''}
                </TableCell>
                <TableCell>{issue.message}</TableCell>
                <TableCell>
                  <Box className={classes.ruleCell}>{issue.rule}</Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
